/**
 * Clock synchronisation — spec 8.1.
 *
 *   C->S  ping { t0 }
 *   S->C  pong { t0, t1 }      t1 = server receive time
 *         t2 = local arrival
 *         rtt    = t2 - t0
 *         offset = t1 - t0 - rtt/2
 *
 * Keep a rolling window of the 8 LOWEST-RTT samples and use their MEDIAN
 * offset. Low-RTT samples are the least jitter-contaminated; the median throws
 * away the one sample that got stuck behind a buffer.
 *
 * INVARIANT 2 — the server is the clock. Everything in the client that has a
 * deadline reads `serverNow()`, never `Date.now()` directly.
 */

import { CLOCK_SYNC_SAMPLE_WINDOW } from '@geogame/shared';

/** How many raw samples are retained before the oldest ages out. */
const RECENT_LIMIT = 24;

export interface ClockSample {
  readonly rttMs: number;
  readonly offsetMs: number;
  /** Local instant the sample completed, used only for ageing. */
  readonly atLocalMs: number;
}

export interface ClockReading {
  readonly offsetMs: number;
  readonly rttMs: number;
  readonly sampleCount: number;
  readonly synced: boolean;
}

function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  if (sorted.length % 2 === 1) return sorted[mid] as number;
  return ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

export class ClockSync {
  private samples: ClockSample[] = [];
  private offsetMs = 0;
  private rttMs = 0;
  private sampled = false;

  /**
   * Seed the offset from a server timestamp that arrived outside the ping
   * handshake (`welcome`, `matched`). One-way, so it carries the downstream
   * latency as error — it is replaced the moment a real sample lands.
   */
  seed(serverTs: number): void {
    if (this.sampled) return;
    this.offsetMs = serverTs - Date.now();
  }

  addSample(t0: number, t1: number, t2: number = Date.now()): ClockSample {
    const rttMs = Math.max(0, t2 - t0);
    const sample: ClockSample = {
      rttMs,
      offsetMs: t1 - t0 - rttMs / 2,
      atLocalMs: t2,
    };
    this.samples.push(sample);
    if (this.samples.length > RECENT_LIMIT) {
      this.samples.splice(0, this.samples.length - RECENT_LIMIT);
    }
    this.recompute();
    this.sampled = true;
    return sample;
  }

  private recompute(): void {
    // The 8 lowest-RTT samples in the retained window, median of their offsets.
    const best = [...this.samples]
      .sort((a, b) => a.rttMs - b.rttMs)
      .slice(0, CLOCK_SYNC_SAMPLE_WINDOW);
    this.offsetMs = median(best.map((s) => s.offsetMs));
    this.rttMs = median(best.map((s) => s.rttMs));
  }

  /** Best estimate of the server's clock right now. */
  serverNow(): number {
    return Date.now() + this.offsetMs;
  }

  /** Convert a server instant into the local clock, for setTimeout arithmetic. */
  toLocal(serverTs: number): number {
    return serverTs - this.offsetMs;
  }

  read(): ClockReading {
    return {
      offsetMs: this.offsetMs,
      rttMs: this.rttMs,
      sampleCount: this.samples.length,
      synced: this.sampled,
    };
  }

  /** Drop history. Used when the identity behind the socket changes. */
  reset(): void {
    this.samples = [];
    this.offsetMs = 0;
    this.rttMs = 0;
    this.sampled = false;
  }
}
